import * as React from "react";
import {
    DefaultButton,
    Pivot,
    PivotItem,
    Stack,
    Separator,
    Spinner,
    ActionButton,
} from "@fluentui/react";
import useAxios from "axios-hooks";
import BasicInfoStep from "./BasicInfoStep";
import { useCallback, useState, useEffect } from "react";
import { Prompt, useHistory, useParams } from "react-router-dom";
import { Formik } from "formik";
import PrimaryButton from "../../../../Forms/PrimaryButton";
import CategoryStep from "./CategoryStep";
import TranscriptStep from "./TranscriptStep";
import ImageStep from "./ImageStep";
import SEOStep from "./SEOStep";
import AuditStep from "./AuditStep";
import OnOffStep from "./OnOffStep";
import ClosedCaptionStep from "./ClosedCaptionStep";
import TranslationsStep from "./TranslationsStep";

export const CreateAudioWizard = () => {
    const history = useHistory();
    const [refreshAudio, setRefreshAudio] = useState(0);

    let { audioId: audioIdFromParams } = useParams();

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const [{ data, loading: submitting }, submitForm] = useAxios(
        { url: `resources/audio` },
        { manual: true });

    const [audioId, setAudioId]  = useState((data && data.audioid) || audioIdFromParams);

    const [
        { data: audioData, loading: audioLoading },
        getAudioData,
    ] = useAxios(
        { url: `resources/audio/${audioId}`, method: "get" },
        { manual: true }
    );


    const [selectedKey, setSelectedKey] = React.useState(0);

    const nextOnClick = useCallback(() => {
        setSelectedKey(Number(selectedKey) + 1);
        setRefreshAudio(refreshAudio+1);
    }, [selectedKey]);

    const prevOnClick = () => {
        setSelectedKey(Number(selectedKey) - 1);
    };

    const handleLinkClick = (item) => {
        const itemKey = item.props.itemKey;
        setSelectedKey(itemKey);
    };

    useEffect(() => {
        if (audioId && !audioLoading) {
            getAudioData();
        }
    }, [
        audioId,
        refreshAudio
    ]);

    if (audioLoading || submitting) {
        return <Spinner className="absolute-center" text="Loading Audio..." />;
    }

    return (
        <Formik
            initialValues={mapInitialValues(audioData)}
            onSubmit={async (values) => {

                if (audioId) {
                    if (selectedKey == 0) {
                        var result = await submitForm({
                            data: values,
                            method: "put",
                            url: `resources/audio/${audioId}/basic-info`,
                        });
                    }

                    //Uncomment once further sections are completed
                    nextOnClick();
                    return;
                }
            
                var result = await submitForm({
                    data: values,
                    method: "post",
                    url: 'resources/audio',
                });

                if (!result.isAxiosError) {
                    setAudioId(result.data);
                    nextOnClick();
                }
            }}
            isInitialValid={false}
        >
            {({ dirty }) => {
                return (
                    <>
                        <Prompt
                            when={(dirty || hasUnsavedChanges) && selectedKey != 10}
                            message="You have unsaved changes, are you sure you want to leave?"
                        />
                        <Pivot
                            selectedKey={String(selectedKey)}
                            onLinkClick={handleLinkClick}
                        >
                            <PivotItem headerText="Basic Info" itemKey="0">
                                <BasicInfoStep
                                    disabled={!!audioId}
                                    audioId = {audioId}
                                    prevOnClick={prevOnClick}
                                    nextOnClick={nextOnClick}
                                />
                            </PivotItem>
                            {audioId && (
                            <PivotItem headerText="Categorization" itemKey="1">
                                <CategoryStep
                                    audioId = {audioId}
                                    prevOnClick={prevOnClick}
                                    nextOnClick={nextOnClick}
                                    audio={audioData}
                                />
                            </PivotItem>)}
                            {audioId && (
                            <PivotItem headerText="Transcript" itemKey="2">
                                <TranscriptStep
                                    audioId = {audioId}
                                    prevOnClick={prevOnClick}
                                    nextOnClick={nextOnClick}
                                    transcript={audioData && audioData.transcript ? audioData.transcript : ""}
                                />
                            </PivotItem>)}
                            {audioId && (
                            <PivotItem headerText="Images" itemKey="3">
                                <ImageStep
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                        audioId={audioId}
                                        audio={audioData}
                                />
                            </PivotItem>)}
                            {audioId && (
                            <PivotItem headerText="SEO" itemKey="4">
                                 <SEOStep
                                    prevOnClick={prevOnClick}
                                    nextOnClick={nextOnClick}
                                    audioId={audioId}
                                        audio={audioData}
                                /> 
                            </PivotItem>)}
                            {audioId && (
                            <PivotItem headerText="Audit Notes" itemKey="5">
                                 <AuditStep
                                    prevOnClick={prevOnClick}
                                    nextOnClick={nextOnClick}
                                    audioId={audioId}
                                        audio={audioData}
                                />
                            </PivotItem>)}
                            {audioId && (
                                <PivotItem headerText="On/Off Settings" itemKey="7">
                                    <OnOffStep
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                        audioId={audioId}
                                        audio={audioData}
                                    >
                                    </OnOffStep>
                                </PivotItem>
                            )}
                            {audioId && (
                            <PivotItem headerText="Closed Captions" itemKey="6">
                                 <ClosedCaptionStep
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                        audioId={audioId}
                                            audio={audioData}
                                    /> 
                            </PivotItem>)}
                            {audioId && (
                                <PivotItem headerText="Translations" itemKey="8">
                                    <TranslationsStep
                                        prevOnClick={prevOnClick}
                                        nextOnClick={nextOnClick}
                                        audioId={audioId}
                                        audio={audioData}
                                    >
                                    </TranslationsStep>
                                </PivotItem>
                            )}
                        </Pivot>
                    </>
                );
            }}
        </Formik>
    );
};

export function mapInitialValues(audioData) {

    if (audioData)
        return {audioInstructorsTmp: [], ...audioData};
    else return {audioInstructorsTmp: [], audioInstructors: []};
}


export const NavigationalButtons = ({
    onDeleteHandler,
    onDeleteProps,
    onNextHandler,
    onNextProps,
    onPrevHandler,
    onPrevProps,
    width,
    loading,
}) => {
    const showPrevious = onPrevHandler || onPrevProps;
    return (
        <>
            <Separator />
            <Stack
                horizontal
                styles={{
                    root: {
                        height: 50,
                        ...(width && { width }),
                    },
                }}
                horizontalAlign={showPrevious ? "space-between" : "end"}
                verticalAlign="center"
            >
                {showPrevious && (
                    <DefaultButton disabled={loading} onClick={onPrevHandler} {...onPrevProps}>
                        {(onPrevProps && onPrevProps.text) || "Prev"}
                    </DefaultButton>
                )}
                <Stack horizontal verticalAlign="center">
                    {onDeleteHandler &&
                        <ActionButton
                            disabled={loading}
                            iconProps={{ iconName: 'Delete' }}
                            onClick={onDeleteHandler}
                            {...onDeleteProps}
                            text="Delete"
                        />
                    }
                    <PrimaryButton
                        text={(onNextProps && onNextProps.text) || "Next"}
                        loading={loading}
                        onClick={onNextHandler}
                        {...onNextProps}
                    /></Stack>
            </Stack>
        </>
    );
};
export default CreateAudioWizard;
